import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Chip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { RestorationAction } from 'models/order';
import { formatCurrency, formatNumber } from 'utils/format';
import { truncateWithEllipsis } from 'utils/action-code';
import BoxWithHighLighting from 'components/with-hith-light-components/box-with-highlighting.component';
import TableCellWithHighLighting from 'components/with-hith-light-components/table-cell-with-highlighting.component';

type RestorationActionsTableProps = {
    rows: RestorationAction[];
    originalRows: RestorationAction[];
    currency: string;
    /** External technicians may be limited to seeing no prices. */
    showPrices: boolean;
    /** Row edit / delete controls (clinic-created orders edit row by row through dialogs). */
    onEditRow?: (row: RestorationAction, index: number) => void;
    onRemoveRow?: (row: RestorationAction, index: number) => void;
    disableDelete?: boolean;
    dataTestPrefix?: string;
};

/**
 * Read-only table over `restoration.actions`. The row is frozen; `catalogue` carries the
 * live bits the grid always showed (internal code for the lab, quotable).
 */
const RestorationActionsTable: FC<RestorationActionsTableProps> = ({
    rows,
    originalRows,
    currency,
    showPrices,
    onEditRow,
    onRemoveRow,
    disableDelete,
    dataTestPrefix = 'restoration-actions',
}) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const hasControls = Boolean(onEditRow || onRemoveRow);

    return (
        <TableContainer>
            <Table size="small">
                <TableHead sx={{ borderBottom: 'none' }}>
                    <TableRow>
                        <TableCell>{translate('orders:ACTIONS')}</TableCell>
                        <TableCell sx={{ width: 50 }} align="right">
                            {translate('orders:QUANTITY')}
                        </TableCell>
                        {showPrices && (
                            <TableCell sx={{ width: 200 }} align="right">
                                {translate('orders:TOTAL_INCL_VAT')}
                            </TableCell>
                        )}
                        {hasControls && <TableCell sx={{ width: 100 }}></TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length ? (
                        rows.map((row, index) => {
                            const original = originalRows[index];
                            return (
                                <TableRow key={row._id}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" flexWrap="wrap">
                                            <BoxWithHighLighting
                                                mr={1}
                                                data={row.code || ''}
                                                originalData={original?.code || ''}
                                            />
                                            {row.internalCode && (
                                                <Tooltip title={row.internalCode}>
                                                    <Chip
                                                        label={truncateWithEllipsis(row.internalCode, 20)}
                                                        size="small"
                                                    />
                                                </Tooltip>
                                            )}
                                            <BoxWithHighLighting
                                                mx={1}
                                                data={row.name}
                                                originalData={original?.name || ''}
                                            />
                                            <BoxWithHighLighting
                                                mr={1}
                                                data={row.categoryName || ''}
                                                originalData={original?.categoryName || ''}
                                            />
                                            {row.quotable ? (
                                                <Chip label={translate('common:QUOTABLE')} size="small" />
                                            ) : undefined}
                                        </Box>
                                    </TableCell>
                                    <TableCellWithHighLighting
                                        align="right"
                                        data={formatNumber(row.price.quantity)}
                                        originalData={original ? formatNumber(original.price.quantity) : ''}
                                    />
                                    {showPrices && (
                                        <TableCellWithHighLighting
                                            align="right"
                                            data={formatCurrency(row.price.totalWithVat, currency)}
                                            originalData={
                                                original ? formatCurrency(original.price.totalWithVat, currency) : ''
                                            }
                                        />
                                    )}
                                    {hasControls && (
                                        <TableCell align="right">
                                            <Fragment>
                                                {onEditRow && (
                                                    <IconButton
                                                        onClick={() => onEditRow(row, index)}
                                                        aria-label="edit"
                                                        size="small"
                                                        data-test={`${dataTestPrefix}.edit-action-${index}`}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                )}
                                                {onRemoveRow && (
                                                    <IconButton
                                                        onClick={() => onRemoveRow(row, index)}
                                                        aria-label="delete"
                                                        size="small"
                                                        disabled={disableDelete}
                                                        data-test={`${dataTestPrefix}.delete-action-${index}`}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Fragment>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={8}>
                                <Typography variant="body2" color="#666">
                                    {translate('orders:NO_ADDED_ACTIONS')}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default RestorationActionsTable;
